import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';

const PRIORITY_COLORS = { High: 'var(--danger)', Med: 'var(--warn)', Low: 'var(--text-muted)' };

// Local copy retained for one-time migration only — see migrateLocalStorage()
const LEGACY_STORAGE_KEY = 'majans_recommendations_v1';

function groupBySection(items) {
  // Items arrive flat from the API; rebuild the {section, color, items[]}
  // shape the UI expects. Preserves API-side ordering (Section, SortOrder).
  const map = new Map();
  for (const item of items) {
    if (!map.has(item.section)) {
      map.set(item.section, {
        section: item.section,
        color: item.sectionColor,
        items: [],
      });
    }
    map.get(item.section).items.push(item);
  }
  return Array.from(map.values());
}

export default function Recommendations() {
  const apiFetch = useApi();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('All');
  const migratedRef = useRef(false);

  const { data: items = [], isLoading, error } = useQuery({
    queryKey: ['recommendations'],
    queryFn: async () => {
      const res = await apiFetch('/recommendations');
      if (!res.ok) throw new Error(`Failed to load recommendations: ${res.status}`);
      return res.json();
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await apiFetch(`/recommendation-status/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`Failed to update status: ${res.status}`);
      return res.json();
    },
    onMutate: async ({ id, status }) => {
      // Optimistic update — UI feels instant, rolls back on error
      await queryClient.cancelQueries({ queryKey: ['recommendations'] });
      const prev = queryClient.getQueryData(['recommendations']);
      queryClient.setQueryData(['recommendations'], (old = []) =>
        old.map(r => (r.id === id ? { ...r, status } : r)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['recommendations'], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['recommendations'] }),
  });

  // One-time migration: read any legacy localStorage state, push to API, then drop the local copy.
  // Runs once after the first successful API fetch — only if we haven't already migrated.
  useEffect(() => {
    if (migratedRef.current || items.length === 0) return;
    let raw;
    try { raw = localStorage.getItem(LEGACY_STORAGE_KEY); } catch { return; }
    if (!raw) { migratedRef.current = true; return; }

    let legacy;
    try { legacy = JSON.parse(raw); } catch { localStorage.removeItem(LEGACY_STORAGE_KEY); migratedRef.current = true; return; }

    const entries = Object.entries(legacy).filter(([, status]) => status === 'done' || status === 'na');
    if (entries.length === 0) { localStorage.removeItem(LEGACY_STORAGE_KEY); migratedRef.current = true; return; }

    migratedRef.current = true;
    Promise.all(entries.map(([id, status]) =>
      apiFetch(`/recommendation-status/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }).catch(() => null),
    )).then(() => {
      try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* ignore */ }
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
    });
  }, [items.length, apiFetch, queryClient]);

  const cycleCheck = (id, currentStatus, e) => {
    e.stopPropagation();
    updateStatus.mutate({ id, status: currentStatus === 'done' ? 'open' : 'done' });
  };
  const toggleNA = (id, currentStatus, e) => {
    e.stopPropagation();
    updateStatus.mutate({ id, status: currentStatus === 'na' ? 'open' : 'na' });
  };

  const openCount = items.filter(i => i.status === 'open').length;
  const doneCount = items.filter(i => i.status === 'done').length;
  const naCount   = items.filter(i => i.status === 'na').length;
  const total     = items.length;

  const sections = useMemo(() => {
    const grouped = groupBySection(items);
    return grouped
      .map(s => ({
        ...s,
        items: s.items.filter(i => {
          if (filter === 'All')  return true;
          if (filter === 'Open') return i.status === 'open';
          if (filter === 'Done') return i.status === 'done';
          if (filter === 'N/A')  return i.status === 'na';
          return i.priority === filter;
        }),
      }))
      .filter(s => s.items.length > 0);
  }, [items, filter]);

  if (isLoading) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading recommendations…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--danger)' }}>
        Failed to load recommendations: {error.message}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>Insights &amp; Recommendations</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {openCount} open · {doneCount} complete · {naCount} N/A · synced to your account
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Show</span>
          {['All', 'Open', 'Done', 'N/A', 'High', 'Med', 'Low'].map(f => (
            <button key={f} className={filter === f ? 'pill-active' : 'pill-inactive'} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12 }}>
          <span style={{ color: 'var(--text-body)' }}>Overall progress</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            {doneCount} complete · {naCount} N/A · {openCount} remaining
          </span>
        </div>
        <div style={{ display: 'flex', height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${total ? (doneCount / total) * 100 : 0}%`, height: '100%', background: 'var(--success)', transition: 'width 0.3s ease' }} />
          <div style={{ width: `${total ? (naCount / total) * 100 : 0}%`, height: '100%', background: 'var(--text-muted)', transition: 'width 0.3s ease' }} />
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 11 }}>
          <span style={{ color: 'var(--success)' }}>■ Complete</span>
          <span style={{ color: 'var(--text-muted)' }}>■ N/A</span>
          <span style={{ color: 'var(--border-soft)' }}>■ Open</span>
        </div>
      </div>

      {/* Sections */}
      {sections.map(section => (
        <div key={section.section} className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 3, height: 18, background: section.color, borderRadius: 2 }} />
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{section.section}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
              {section.items.filter(i => i.status === 'done').length} done ·{' '}
              {section.items.filter(i => i.status === 'na').length} N/A
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {section.items.map(item => {
              const isDone = item.status === 'done';
              const isNA   = item.status === 'na';

              const cardBg     = isDone ? 'var(--bg-elevated)' : isNA ? 'var(--bg)' : 'var(--bg-card)';
              const cardBorder = isDone ? 'var(--border)' : isNA ? 'var(--border)' : 'var(--border-soft)';
              const cardOpacity = (isDone || isNA) ? 0.55 : 1;

              return (
                <div key={item.id} style={{
                  display: 'flex', gap: 14, padding: '14px 16px',
                  background: cardBg, border: `1px solid ${cardBorder}`,
                  borderRadius: 8, opacity: cardOpacity, transition: 'all 0.15s',
                }}>
                  {/* Checkbox */}
                  <div onClick={e => cycleCheck(item.id, item.status, e)}
                    title={isDone ? 'Mark as open' : 'Mark as complete'}
                    style={{
                      width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 1, cursor: 'pointer',
                      border: `2px solid ${isDone ? 'var(--success)' : 'var(--text-muted)'}`,
                      background: isDone ? 'var(--success)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    {isDone && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 13, fontWeight: 600,
                        color: (isDone || isNA) ? 'var(--text-muted)' : 'var(--text-primary)',
                        textDecoration: isDone ? 'line-through' : 'none',
                      }}>{item.title}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                        color: PRIORITY_COLORS[item.priority],
                        background: `color-mix(in srgb, ${PRIORITY_COLORS[item.priority]} 14%, transparent)`,
                        borderRadius: 4, padding: '2px 6px',
                      }}>{item.priority}</span>
                      {isNA && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--border)', borderRadius: 4, padding: '2px 6px' }}>N/A</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-body)', lineHeight: 1.6 }}>{item.detail}</div>
                    {item.source && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                        Source: <span style={{ color: 'var(--text-muted)' }}>{item.source}</span>
                      </div>
                    )}
                  </div>

                  {/* Irrelevant / Dismiss */}
                  <button onClick={e => toggleNA(item.id, item.status, e)}
                    title={isNA ? 'Mark as open' : 'Dismiss — not relevant'}
                    style={{
                      flexShrink: 0, alignSelf: 'flex-start', marginTop: 1,
                      background: isNA ? 'var(--border)' : 'transparent',
                      border: `1px solid ${isNA ? 'var(--text-muted)' : 'var(--border-soft)'}`,
                      borderRadius: 4, padding: '2px 8px',
                      color: isNA ? 'var(--text-body)' : 'var(--text-muted)',
                      fontSize: 10, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.04em',
                    }}>
                    {isNA ? 'Restore' : 'Irrelevant'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {sections.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          No recommendations match the selected filter.
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', paddingBottom: 8 }}>
        Click the checkbox to mark complete · Click Irrelevant to dismiss · Synced across your devices
      </div>
    </div>
  );
}
